import type { CaptureResult, Properties } from "posthog-js";
import {
  redactStripeSecretParams,
  STRIPE_SECRET_URL_PARAMS,
} from "@/lib/stripe-url-params";

const redactProperties = (properties: Properties): Properties => {
  let redacted: Properties | undefined;
  for (const [key, value] of Object.entries(properties)) {
    if (typeof value !== "string") {
      continue;
    }
    const clean = redactStripeSecretParams(value);
    if (clean !== value) {
      redacted ??= { ...properties };
      redacted[key] = clean;
    }
  }
  return redacted ?? properties;
};

/**
 * Last-chance scrub of an outgoing event, so a Stripe client secret can never be
 * stored in PostHog even if it reached the page before the URL was cleaned.
 *
 * The case this exists for is `$referrer` / `$initial_referrer`: posthog-js builds
 * both from `document.referrer` verbatim, which no amount of rewriting the current
 * URL can change, and which a tab still open on a pre-fix checkout URL will carry.
 */
export const sanitizeCaptureProperties = (
  event: CaptureResult | null
): CaptureResult | null => {
  if (!event) {
    return event;
  }
  const properties = redactProperties(event.properties);
  const set = event.$set ? redactProperties(event.$set) : undefined;
  const setOnce = event.$set_once
    ? redactProperties(event.$set_once)
    : undefined;
  if (
    properties === event.properties &&
    set === event.$set &&
    setOnce === event.$set_once
  ) {
    return event;
  }
  return {
    ...event,
    properties,
    ...(set ? { $set: set } : {}),
    ...(setOnce ? { $set_once: setOnce } : {}),
  };
};

export const POSTHOG_CONFIG = {
  api_host: "https://p.arcade.dev",
  ui_host: "https://us.posthog.com",
  capture_pageview: false,
  capture_pageleave: false,
  session_recording: {
    maskTextSelector: ".sensitive",
  },
  before_send: sanitizeCaptureProperties,
  // Covers the URLs posthog-js builds itself, which `before_send` can't reach:
  // session replay stores the page URL in rrweb snapshot metadata, and the person
  // property URL is rebuilt from a cookie. This also masks ad-click ids (gclid,
  // fbclid), which this dashboard does not attribute against.
  mask_personal_data_properties: true,
  custom_personal_data_properties: [...STRIPE_SECRET_URL_PARAMS],
};
